import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import Datepicker from '../form-elements/datepicker.component';
import { formatDate } from '../../utils/datetime.util';
import {
  deleteNewChannelReleaseById,
  saveNewChannelRelease,
} from '../../services/ruleset.service';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const NewChannelRelease = ({
  title,
  channelId,
  onCancel,
  onSuccess,
  newChannelReleaseInfo,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      startDate: newChannelReleaseInfo?.dateStart || null,
      numberOfDays: newChannelReleaseInfo?.daysToBlockContent || '',
      blackoutAlwaysOnVideos:
        newChannelReleaseInfo?.blackoutAlwaysOnVideos || 0,
    },
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [confirmationText, setConfirmationText] = useState('');

  const handleDeleteData = () => {
    setFormSubmitted(true);

    deleteNewChannelReleaseById(newChannelReleaseInfo.id)
      .then(() => {
        onSuccess('New channel release removed successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      startDate: formatDate(data.startDate, 'YYYY-MM-DD'),
      numberOfDays: Number(data.numberOfDays) || 0,
      blackoutAlwaysOnVideos: Number(data.blackoutAlwaysOnVideos),
    };
    saveNewChannelRelease(channelId, payload)
      .then(() => {
        onSuccess('New channel release saved successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12}>
              <Typography variant="body2" color="secondary">
                Set start date (date when app is/will be released) and number of
                days (days from start date) to restrict this channel videos to
                be scheduled on other apps.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Controller
                name="startDate"
                control={control}
                rules={validationRules.REQUIRED}
                render={({ field: { onChange, value } }) => (
                  <Datepicker
                    id="start_date"
                    name="start_date"
                    label="Start Date *"
                    defaultValue={value}
                    clearable
                    onChange={onChange}
                    sx={{ width: '100%', p: 0 }}
                    error={errors?.startDate?.message || ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Controller
                name="numberOfDays"
                control={control}
                rules={validationRules.REQUIRED}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Number of Days *"
                    type="number"
                    error={!!errors.numberOfDays}
                    helperText={errors?.numberOfDays?.message || null}
                    fullWidth
                    variant="filled"
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Controller
                name="blackoutAlwaysOnVideos"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    style={{ width: '400px' }}
                    label="Blackout Always-On Videos?"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Box width="100%" display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  form="add-edit-form"
                  color="primary"
                  variant="contained"
                  disabled={formSubmitted}
                >
                  {`${
                    !newChannelReleaseInfo ? 'Set As' : 'Modify'
                  } New Channel Release`}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {newChannelReleaseInfo && newChannelReleaseInfo.id && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12}>
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1">
                Reset New Channel Settings
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="secondary" sx={{ mb: 1 }}>
                  If you want to removed this channel from new channel release
                  settings immediately, type{' '}
                  <b>
                    <i>remove</i>
                  </b>{' '}
                  in below input and proceed:
                </Typography>
                <TextField
                  label=""
                  type="text"
                  onChange={(e) => setConfirmationText(e.target.value)}
                  value={confirmationText}
                  fullWidth
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              <Box width="100%" display="flex" justifyContent="flex-end">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => handleDeleteData()}
                  disabled={formSubmitted || confirmationText !== 'remove'}
                >
                  Reset Settings
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

NewChannelRelease.propTypes = {
  title: PropTypes.string.isRequired,
  channelId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  newChannelReleaseInfo: PropTypes.object.isRequired,
};

export default NewChannelRelease;
